import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Collapse,
  IconButton,
  Typography
} from '@mui/material';
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Payments as PaymentsIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import api from '../services/api';
import { colors } from '../styles';

const DONUT_SIZE = 200;

const formatCurrency = (value) => Number(value || 0).toLocaleString(undefined, {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const sharePercent = (amount, total) => {
  if (!(total > 0)) return 0;
  return Math.round((Number(amount || 0) / total) * 100);
};

const pluralizeAccounts = (count) => `${count} ${count === 1 ? 'account' : 'accounts'}`;

const PurchasingPowerCard = ({ refreshKey = 0 }) => {
  const [purchasingPower, setPurchasingPower] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSegment, setExpandedSegment] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/accounts/purchasing-power');
      setPurchasingPower({
        total: response.data?.total || 0,
        segments: response.data?.segments || []
      });
    } catch (requestError) {
      const message = requestError?.response?.data?.error;
      setError(typeof message === 'string' ? message : 'Purchasing power is unavailable.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const segments = useMemo(() => purchasingPower?.segments || [], [purchasingPower]);
  const total = purchasingPower?.total || 0;
  const accountCount = useMemo(
    () => segments.reduce((sum, segment) => sum + (segment.accounts?.length || 0), 0),
    [segments]
  );
  const chartData = useMemo(
    () => segments.map(segment => ({
      name: segment.label,
      value: Math.max(0, Number(segment.amount) || 0),
      color: segment.color
    })),
    [segments]
  );

  if (loading && !purchasingPower) {
    return (
      <Card sx={{ borderRadius: '20px', boxShadow: 'none' }}>
        <CardContent sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={24} sx={{ color: colors.primary }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Purchasing power</Typography>
            <Typography variant="body2" color="text.secondary">
              Adding up what you can spend right now
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error && !purchasingPower) {
    return (
      <Card sx={{ borderRadius: '20px', boxShadow: 'none' }}>
        <CardContent sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Purchasing power unavailable</Typography>
            <Typography variant="body2" color="text.secondary">{error}</Typography>
          </Box>
          <Button startIcon={<RefreshIcon />} onClick={load}>Try again</Button>
        </CardContent>
      </Card>
    );
  }

  // No spendable balances anywhere — the Money page already prompts to connect
  // accounts, so an empty donut would only add noise.
  if (!segments.length) {
    return null;
  }

  return (
    <Card sx={{ borderRadius: '20px', boxShadow: 'none' }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 2 }}>
          <Box
            sx={{
              alignItems: 'center',
              backgroundColor: `${colors.primary}14`,
              borderRadius: '14px',
              color: colors.primary,
              display: 'flex',
              height: 48,
              justifyContent: 'center',
              width: 48
            }}
          >
            <PaymentsIcon />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Purchasing power</Typography>
            <Typography variant="body2" color="text.secondary">
              Spendable now across {pluralizeAccounts(accountCount)}
            </Typography>
          </Box>
        </Box>

        <Box
          aria-label={`${formatCurrency(total)} of purchasing power across ${pluralizeAccounts(accountCount)}`}
          role="img"
          sx={{ height: DONUT_SIZE, mt: 2, position: 'relative', width: '100%' }}
        >
          <ResponsiveContainer height="100%" width="100%">
            <PieChart>
              <Pie
                cx="50%"
                cy="50%"
                data={chartData}
                dataKey="value"
                endAngle={-270}
                innerRadius={64}
                isAnimationActive={false}
                outerRadius={88}
                paddingAngle={0}
                startAngle={90}
                stroke="none"
              >
                {chartData.map(entry => (
                  <Cell fill={entry.color} key={entry.name} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              flexDirection: 'column',
              inset: 0,
              justifyContent: 'center',
              pointerEvents: 'none',
              position: 'absolute'
            }}
          >
            <Typography variant="h5" sx={{ color: colors.secondary, fontWeight: 700 }}>
              {formatCurrency(total)}
            </Typography>
            <Typography variant="caption" color="text.secondary">available</Typography>
          </Box>
        </Box>

        {error ? (
          <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between', gap: 2, mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Couldn't refresh. Showing the last update.
            </Typography>
            <Button size="small" startIcon={<RefreshIcon />} onClick={load}>Try again</Button>
          </Box>
        ) : null}

        <Box sx={{ mt: 2 }}>
          {segments.map((segment) => {
            const isExpanded = expandedSegment === segment.type;
            const percent = sharePercent(segment.amount, total);
            const segmentAccounts = segment.accounts || [];

            return (
              <Box key={segment.type} sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.5, py: 1.5 }}>
                  <Box sx={{ backgroundColor: segment.color, borderRadius: '50%', flexShrink: 0, height: 12, width: 12 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body1" noWrap sx={{ fontWeight: 700 }}>
                      {segment.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {percent}% • {pluralizeAccounts(segmentAccounts.length)}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {formatCurrency(segment.amount)}
                  </Typography>
                  <IconButton
                    aria-expanded={isExpanded}
                    aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${segment.label} accounts`}
                    onClick={() => setExpandedSegment(isExpanded ? null : segment.type)}
                    size="small"
                  >
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>

                <Collapse in={isExpanded}>
                  <Box sx={{ pb: 2, pl: 3.5 }}>
                    {segmentAccounts.map((account) => (
                      <Box
                        key={account.accountId}
                        sx={{ alignItems: 'center', display: 'flex', gap: 2, justifyContent: 'space-between', py: 0.5 }}
                      >
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {account.name}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {formatCurrency(account.available)}
                        </Typography>
                      </Box>
                    ))}
                    {segment.type === 'credit' ? (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Credit available is borrowing room, not cash you own.
                      </Typography>
                    ) : null}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
};

export default PurchasingPowerCard;
